import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Props for the Calendar component
 */
export interface CalendarProps {
    /** The currently selected date */
    selected?: Date;
    /** Callback fired when a date is selected */
    onSelect?: (date: Date | undefined) => void;
    /** Function to determine if a date should be disabled */
    disabled?: (date: Date) => boolean;
    /** Additional CSS classes to apply to the calendar */
    className?: string;
    /**
     * Whether to use a fixed height for the calendar grid.
     * When true, always renders 6 weeks (42 cells) to prevent height changes.
     * Useful when the calendar appears above its trigger to prevent layout shift.
     */
    fixedHeight?: boolean;
    /**
     * The default month to display when no date is selected.
     * Useful for opening the calendar to a contextually relevant month.
     */
    defaultMonth?: Date;
}

/**
 * Gets the number of days in a given month
 * @param date - The date to get the month from
 * @returns The number of days in the month
 */
const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Gets the day of the week for the first day of a given month
 * @param date - The date to get the month from
 * @returns The day of the week (0 = Sunday, 1 = Monday, etc.)
 */
const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

/**
 * Checks if two dates are the same day
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns True if both dates are the same day
 */
const isSameDay = (date1: Date, date2: Date) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

/**
 * Checks if a date is today
 * @param date - The date to check
 * @returns True if the date is today
 */
const isToday = (date: Date) => {
    const today = new Date();
    return isSameDay(date, today);
};

/**
 * Formats a date to display month and year
 * @param date - The date to format
 * @returns Formatted month and year string (e.g., "January 2024")
 */
const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
};

/**
 * A customizable calendar component for date selection.
 *
 * Features:
 * - Month navigation with previous/next buttons
 * - Day selection with visual feedback
 * - Today highlighting
 * - Disabled date support
 * - Responsive grid layout
 *
 * @example
 * ```tsx
 * <Calendar
 *   selected={selectedDate}
 *   onSelect={setSelectedDate}
 *   disabled={(date) => date < new Date()}
 * />
 * ```
 */
export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
    (
        {
            selected,
            onSelect,
            disabled,
            className,
            fixedHeight = false,
            defaultMonth,
        },
        ref,
    ) => {
        const [currentMonth, setCurrentMonth] = React.useState(
            selected || defaultMonth || new Date(),
        );

        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        const handlePreviousMonth = () => {
            setCurrentMonth(
                (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
            );
        };

        const handleNextMonth = () => {
            setCurrentMonth(
                (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
            );
        };

        const handleDateClick = (day: number) => {
            const date = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day,
            );
            if (disabled && disabled(date)) return;
            onSelect?.(date);
        };

        const renderCalendarDays = () => {
            const daysInMonth = getDaysInMonth(currentMonth);
            const firstDay = getFirstDayOfMonth(currentMonth);
            const days: React.ReactElement[] = [];

            // Add empty cells for days before the first day of the month
            for (let i = 0; i < firstDay; i++) {
                days.push(
                    <div key={`empty-before-${i}`} className="h-9 w-9" />,
                );
            }

            // Add days of the month
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(
                    currentMonth.getFullYear(),
                    currentMonth.getMonth(),
                    day,
                );
                const isSelected = selected && isSameDay(date, selected);
                const isTodayDate = isToday(date);
                const isDisabled = disabled && disabled(date);

                days.push(
                    <button
                        key={day}
                        type="button"
                        className={cn(
                            'focus:outline-hidden h-9 w-9 rounded-md text-sm transition-colors hover:bg-gray-100 focus:bg-gray-100',
                            isSelected &&
                                'hover:bg-primary/90 bg-primary text-primary-foreground',
                            isTodayDate &&
                                !isSelected &&
                                'bg-gray-100 font-medium',
                            isDisabled &&
                                'cursor-not-allowed opacity-50 hover:bg-transparent',
                        )}
                        onClick={() => handleDateClick(day)}
                        disabled={isDisabled}
                    >
                        {day}
                    </button>,
                );
            }

            // When fixedHeight is enabled, add empty cells to always fill 6 weeks (42 cells)
            // This prevents height changes when navigating between months
            if (fixedHeight) {
                const totalCells = 42; // 6 weeks × 7 days
                const cellsUsed = firstDay + daysInMonth;
                const remainingCells = totalCells - cellsUsed;
                for (let i = 0; i < remainingCells; i++) {
                    days.push(
                        <div key={`empty-after-${i}`} className="h-9 w-9" />,
                    );
                }
            }

            return days;
        };

        return (
            <div ref={ref} className={cn('w-full', className)}>
                {/* Header */}
                <div className="mb-4 flex items-center justify-between border-b border-gray-200 px-4 py-2">
                    <button
                        type="button"
                        onClick={handlePreviousMonth}
                        className="focus:outline-hidden h-8 w-8 rounded-md hover:bg-gray-100 focus:bg-gray-100"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <h3 className="text-sm font-medium">
                        {formatMonthYear(currentMonth)}
                    </h3>
                    <button
                        type="button"
                        onClick={handleNextMonth}
                        className="focus:outline-hidden h-8 w-8 rounded-md hover:bg-gray-100 focus:bg-gray-100"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>

                {/* Day names */}
                <div className="grid grid-cols-7 gap-1 px-4">
                    {dayNames.map((day) => (
                        <div
                            key={day}
                            className="h-5 w-9 text-center text-xs font-medium text-gray-500"
                        >
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar days */}
                <div
                    className="grid grid-cols-7 gap-1 px-4 pb-2"
                    {...(fixedHeight && {
                        style: { minHeight: '236px' }, // 6 rows × 36px (h-9) + 5 gaps × 4px (gap-1) = 216px + 20px = 236px
                    })}
                >
                    {renderCalendarDays()}
                </div>
            </div>
        );
    },
);

Calendar.displayName = 'Calendar';
